import { Component, Input, Output, EventEmitter, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { KsefInvoice, KsefInvoiceStatus } from '../../models/ksef-invoice.model';
import { KsefApiService } from '../../services/ksef-api.service';
import { ToastrService } from 'ngx-toastr';

const DEFAULT_PAGE_SIZE = 50;
const PAGE_SIZE_OPTIONS = [5, 10, 20, 50, 10000];

type SortColumn =
  | 'ksefNumber'
  | 'vendorInvoiceNumber'
  | 'contractorName'
  | 'grossAmount'
  | 'currency'
  | 'issueDate'
  | 'dueDate'
  | 'isPaid'
  | 'status'
  | 'employeeIdentifier'
  | 'costCreatorName'
  | 'createdAt';

type SortDirection = 'asc' | 'desc' | null;

@Component({
  selector: 'app-ksef-invoices-table',
  templateUrl: './ksef-invoices-table.component.html',
  styles: [`
    .ksef-row--highlight td, .ksef-table__card--highlight {
      background-color: #fef08a !important;
      transition: background-color 0.5s ease;
    }
  `]
})
export class KsefInvoicesTableComponent implements OnInit, OnDestroy {
  @Input() invoices: KsefInvoice[] | null = [];
  @Input() scope: 'general' | 'my' | 'cancelled' | 'in_circulation' | 'hrnest_settled' = 'general';
  @Output() refresh = new EventEmitter<void>();

  pageSize = DEFAULT_PAGE_SIZE;
  pageIndex = 0;
  pageSizeOptions = PAGE_SIZE_OPTIONS;

  // Sort
  sortColumn: SortColumn | null = 'createdAt';
  sortDirection: SortDirection = 'desc';

  highlightId: number | null = null;
  private highlightTimeout: any;

  constructor(
    private ksefApi: KsefApiService, 
    private toastr: ToastrService,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      const highlightKsefId = params['highlightKsefId'];
      if (highlightKsefId) {
        this.highlightId = Number(highlightKsefId);
        if (this.highlightTimeout) {
          clearTimeout(this.highlightTimeout);
        }
        this.highlightTimeout = setTimeout(() => {
          this.router.navigate([], {
            relativeTo: this.route,
            queryParams: { highlightKsefId: null },
            queryParamsHandling: 'merge',
            replaceUrl: true
          });
        }, 3000);
      }
    });
  }

  ngOnDestroy(): void {
    if (this.highlightTimeout) {
      clearTimeout(this.highlightTimeout);
    }
  }

  statusClass(status: string): string {
    return (status || '').toLowerCase();
  }

  get loading(): boolean {
    return this.invoices === null || this.invoices === undefined;
  }

  get rawList(): KsefInvoice[] {
    return this.invoices != null ? this.invoices : [];
  }

  /** Sorted list */
  get list(): KsefInvoice[] {
    const data = [...this.rawList];
    if (!this.sortColumn || !this.sortDirection) { return data; }

    const col = this.sortColumn;
    const dir = this.sortDirection === 'asc' ? 1 : -1;

    return data.sort((a, b) => {
      const aVal = this.getSortValue(a, col);
      const bVal = this.getSortValue(b, col);

      if (aVal == null && bVal == null) { return 0; }
      if (aVal == null) { return 1; }
      if (bVal == null) { return -1; }

      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return (aVal - bVal) * dir;
      }
      if (typeof aVal === 'boolean' && typeof bVal === 'boolean') {
        return ((aVal ? 1 : 0) - (bVal ? 1 : 0)) * dir;
      }
      return String(aVal).localeCompare(String(bVal), 'pl') * dir;
    });
  }

  get totalCount(): number {
    return this.list.length;
  }

  get pageCount(): number {
    return this.pageSize > 0 ? Math.ceil(this.totalCount / this.pageSize) : 0;
  }

  get paginatedList(): KsefInvoice[] {
    const start = this.pageIndex * this.pageSize;
    return this.list.slice(start, start + this.pageSize);
  }

  // -- sorting --
  toggleSort(column: SortColumn): void {
    if (this.sortColumn === column) {
      // cycle: asc → desc → none
      if (this.sortDirection === 'asc') {
        this.sortDirection = 'desc';
      } else if (this.sortDirection === 'desc') {
        this.sortColumn = null;
        this.sortDirection = null;
      }
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }
    this.pageIndex = 0;
  }

  sortIcon(column: SortColumn): string {
    if (this.sortColumn !== column) { return '↕'; }
    return this.sortDirection === 'asc' ? '↑' : '↓';
  }

  // -- pagination --
  goToPrev(): void {
    if (this.pageIndex > 0) { this.pageIndex--; }
  }

  goToNext(): void {
    if (this.pageIndex < this.pageCount - 1) { this.pageIndex++; }
  }

  onPageSizeChange(value: number | string): void {
    this.pageSize = typeof value === 'string' ? parseInt(value, 10) : value;
    this.pageIndex = 0;
  }

  // -- actions --
  assignToMe(invoice: KsefInvoice): void {
    if (!confirm(`Czy na pewno chcesz przypisać fakturę ${invoice.ksefNumber} do siebie?`)) { return; }
    this.ksefApi.assignInvoice(invoice.id).subscribe(
      () => {
        this.toastr.success('Faktura została przypisana do Twojej listy.');
        if (this.invoices) { this.invoices = this.invoices.filter(i => i.id !== invoice.id); }
        this.refresh.emit();
      },
      (err) => { this.toastr.error('Nie udało się przypisać faktury.'); console.error(err); }
    );
  }

  unassign(invoice: KsefInvoice): void {
    if (!confirm(`Czy na pewno chcesz odpiąć fakturę ${invoice.ksefNumber}?`)) { return; }
    this.ksefApi.unassignInvoice(invoice.id).subscribe(
      () => {
        this.toastr.success('Faktura została odpięta.');
        if (this.invoices) { this.invoices = this.invoices.filter(i => i.id !== invoice.id); }
        this.refresh.emit();
      },
      (err) => { this.toastr.error('Nie udało się odpiąć faktury.'); console.error(err); }
    );
  }

  requestReturn(invoice: KsefInvoice): void {
    if (!confirm(`Czy na pewno chcesz zgłosić fakturę ${invoice.ksefNumber} do zwrotu do CheckApp?`)) { return; }
    this.ksefApi.requestReturnInvoice(invoice.id).subscribe(
      () => { this.toastr.success('Faktura została zgłoszona do zwrotu.'); invoice.isReturnRequested = true; },
      (err) => { this.toastr.error('Nie udało się zgłosić faktury do zwrotu.'); console.error(err); }
    );
  }

  cancelHrnest(invoice: KsefInvoice): void {
    if (!confirm(`Czy na pewno chcesz anulować fakturę ${invoice.ksefNumber} (rozliczona w HRnest)?`)) { return; }
    this.ksefApi.cancelHrnestInvoice(invoice.id).subscribe(
      () => {
        this.toastr.success('Faktura została anulowana i oznaczona jako rozliczona w HRnest.');
        if (this.invoices) { this.invoices = this.invoices.filter(i => i.id !== invoice.id); }
        this.refresh.emit();
      },
      (err) => { this.toastr.error('Nie udało się anulować faktury.'); console.error(err); }
    );
  }

  // -- helpers --
  private getSortValue(inv: KsefInvoice, col: SortColumn): string | number | boolean | null {
    switch (col) {
      case 'ksefNumber': return inv.ksefNumber || null;
      case 'vendorInvoiceNumber': return inv.vendorInvoiceNumber || null;
      case 'contractorName': return inv.contractorName || null;
      case 'grossAmount': return inv.grossAmount;
      case 'currency': return inv.currency || null;
      case 'issueDate': return inv.issueDate || null;
      case 'dueDate': return inv.dueDate || null;
      case 'isPaid': return inv.isPaid;
      case 'status': return inv.status || null;
      case 'employeeIdentifier': return inv.employeeIdentifier || null;
      case 'costCreatorName': return inv.costCreatorName || null;
      case 'createdAt': return inv.createdAt || null;
      default: return null;
    }
  }
}
